import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Popover } from '@mui/material';
import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import SearchIcon from '@mui/icons-material/Search';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import url from '../../../../serverUrl';
import { api as axios } from '../../../../api';
import { fmtPct, fmtPrice, fmtUsd, lsGet, lsSet, quoteSymbolFor, tokenLogo } from './format';
import './pro.css';


// ---------------------------------------------------------------------------
// Market selector: search, favourites, category tabs, live price / 24h
// change / volume per market (from /hootdex/most-active), keyboard friendly.
// Replaces the plain <select> that listed every token in one long column.
// ---------------------------------------------------------------------------

const CATEGORIES = [
  ['synthetics', 'SynthCryptos'],
  ['crypto_pairs', 'Hybrid'],
  ['hedge', 'Hedge'],
  ['dbt', 'DBT'],
  ['project_tokens', 'Venture'],
  ['holding_tokens', 'Venture'],
  ['fan_tokens', 'Nexus'],
  ['xmg_tokens', 'XMG'],
  ['equitrack_tokens', 'EquiTrack']
];
const FAV_KEY = 'hd_fav_markets';

function Logo({ symbol, size = 22 }) {
  const [bad, setBad] = useState(false);
  if (bad || !symbol) {
    return (
      <span className="x-logo-fallback" style={{ width: size, height: size, fontSize: size * 0.45 }}>
        {(symbol || '?').replace(/^h/, '').slice(0, 2).toUpperCase()}
      </span>
    );
  }
  return (
    <img
      className="x-logo"
      src={tokenLogo(symbol)}
      alt=""
      width={size}
      height={size}
      onError={() => setBad(true)}
    />
  );
}
export { Logo };

export default function PairPicker({ tokenList, value, token, quote, onSelect }) {
  const anchor = useRef(null);
  const searchRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const [cat, setCat] = useState('all');
  const [stats, setStats] = useState({});
  const tickersOk = useRef(false);
  const [favs, setFavs] = useState(() => lsGet(FAV_KEY, []));
  const [cursor, setCursor] = useState(0);
  const [sort, setSort] = useState('volume');

  // Prices for the list, refreshed while the picker is open.
  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;
    let timer;
    // Live prices (same source as the listing pages and the trade screen).
    // Last-fill prices from /most-active are only used until /market-tickers
    // has answered once; after that a failed poll keeps the last good
    // numbers, so the 24h % no longer flips between two sources.
    const load = () =>
      axios
        .get(`${url}/hootdex/market-tickers`, { timeout: 12000 })
        .then((res) => {
          if (cancelled || !Array.isArray(res.data?.tokens) || !res.data.tokens.length) return;
          tickersOk.current = true;
          const m = {};
          res.data.tokens.forEach((t) => (m[t.symbol] = t));
          setStats(m);
        })
        .catch(() => {
          if (cancelled || tickersOk.current) return undefined;
          return axios
            .get(`${url}/hootdex/most-active`, { params: { limit: 100 }, timeout: 12000 })
            .then((res) => {
              if (cancelled || tickersOk.current) return;
              const m = {};
              (res.data?.tokens || []).forEach((t) => (m[t.symbol] = t));
              setStats(m);
            });
        })
        .catch(() => {})
        .finally(() => {
          if (!cancelled) timer = setTimeout(load, 15000);
        });
    load();
    setTimeout(() => searchRef.current?.focus(), 50);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [open]);

  const markets = useMemo(() => {
    const seen = new Set();
    const out = [];
    CATEGORIES.forEach(([key, label]) => {
      (tokenList?.[key] || []).forEach((t) => {
        if (!t?.symbol || seen.has(t.symbol)) return;
        seen.add(t.symbol);
        const m = { symbol: t.symbol, name: t.name || '', cat: label };
        m.quote = quoteSymbolFor(m.symbol);
        out.push(m);
      });
    });
    return out;
  }, [tokenList]);

  const cats = useMemo(() => {
    const s = [];
    markets.forEach((m) => !s.includes(m.cat) && s.push(m.cat));
    return s;
  }, [markets]);

  const rows = useMemo(() => {
    const needle = q.trim().toLowerCase();
    let r = markets.filter((m) => {
      if (cat === 'fav' && !favs.includes(m.symbol)) return false;
      if (cat !== 'all' && cat !== 'fav' && m.cat !== cat) return false;
      if (!needle) return true;
      return m.symbol.toLowerCase().includes(needle) || m.name.toLowerCase().includes(needle);
    });
    const v = (m) => stats[m.symbol];
    r = [...r].sort((a, b) => {
      if (sort === 'name') return a.symbol.localeCompare(b.symbol);
      if (sort === 'change') return (v(b)?.changePct ?? -1e9) - (v(a)?.changePct ?? -1e9);
      return (v(b)?.volume24h ?? -1) - (v(a)?.volume24h ?? -1) || a.symbol.localeCompare(b.symbol);
    });
    return r;
  }, [markets, q, cat, favs, stats, sort]);

  useEffect(() => setCursor(0), [q, cat]);

  const toggleFav = (sym, e) => {
    e.stopPropagation();
    setFavs((f) => {
      const next = f.includes(sym) ? f.filter((x) => x !== sym) : [...f, sym];
      lsSet(FAV_KEY, next);
      return next;
    });
  };

  const choose = (sym) => {
    setOpen(false);
    setQ('');
    if (sym && sym !== value) onSelect(sym);
  };

  const onKey = (e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setCursor((c) => Math.min(rows.length - 1, c + 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setCursor((c) => Math.max(0, c - 1));
    } else if (e.key === 'Enter' && rows[cursor]) {
      choose(rows[cursor].symbol);
    }
  };

  const current = markets.find((m) => m.symbol === value);
  const name = token?.name || token?.token_name || current?.name || '';

  return (
    <>
      <button
        ref={anchor}
        type="button"
        className={open ? 'x-pair x-open' : 'x-pair'}
        onClick={() => setOpen(true)}
        aria-haspopup="listbox"
      >
        <Logo symbol={value} size={28} />
        <span className="x-pair-text">
          <span className="x-pair-sym">
            {value || 'Select market'}
            <span className="x-pair-quote">/{quote || current?.quote || 'PECU'}</span>
          </span>
          <span className="x-pair-name">{name}</span>
        </span>
        {current && <span className="x-tag">{current.cat}</span>}
        <KeyboardArrowDownIcon sx={{ fontSize: 18, color: 'var(--x-muted)' }} />
      </button>

      <Popover
        open={open}
        anchorEl={anchor.current}
        onClose={() => setOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        PaperProps={{ className: 'x-picker' }}
      >
        <div className="x-picker-search">
          <SearchIcon sx={{ fontSize: 18, color: 'var(--x-muted)' }} />
          <input
            ref={searchRef}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={onKey}
            placeholder="Search markets"
            aria-label="Search markets"
          />
          <span className="x-kbd">{markets.length} markets</span>
        </div>
        <div className="x-picker-cats">
          <button type="button" className={cat === 'fav' ? 'x-active' : ''} onClick={() => setCat('fav')}>
            <StarIcon sx={{ fontSize: 14 }} /> Favourites
          </button>
          <button type="button" className={cat === 'all' ? 'x-active' : ''} onClick={() => setCat('all')}>
            All
          </button>
          {cats.map((c) => (
            <button key={c} type="button" className={cat === c ? 'x-active' : ''} onClick={() => setCat(c)}>
              {c}
            </button>
          ))}
        </div>
        <div className="x-picker-head">
          <button type="button" onClick={() => setSort('name')} className={sort === 'name' ? 'x-active' : ''}>Market</button>
          <span>Price (USD)</span>
          <button type="button" onClick={() => setSort('change')} className={sort === 'change' ? 'x-active' : ''}>24h change</button>
          <button type="button" onClick={() => setSort('volume')} className={sort === 'volume' ? 'x-active' : ''}>24h volume</button>
        </div>
        <div className="x-picker-list" role="listbox">
          {rows.map((m, i) => {
            const s = stats[m.symbol];
            const up = (s?.changePct ?? 0) >= 0;
            return (
              <div
                key={m.symbol}
                role="option"
                aria-selected={m.symbol === value}
                className={`x-picker-row${i === cursor ? ' x-cursor' : ''}${m.symbol === value ? ' x-current' : ''}`}
                onMouseEnter={() => setCursor(i)}
                onClick={() => choose(m.symbol)}
              >
                <span className="x-picker-market">
                  <button
                    type="button"
                    className="x-star"
                    onClick={(e) => toggleFav(m.symbol, e)}
                    aria-label={favs.includes(m.symbol) ? 'Remove favourite' : 'Add favourite'}
                  >
                    {favs.includes(m.symbol) ? (
                      <StarIcon sx={{ fontSize: 15, color: '#f4c84a' }} />
                    ) : (
                      <StarBorderIcon sx={{ fontSize: 15 }} />
                    )}
                  </button>
                  <Logo symbol={m.symbol} size={20} />
                  <span className="x-picker-sym">
                    <b>
                      {m.symbol}
                      <span className="x-pair-quote">/{stats[m.symbol]?.quote || m.quote}</span>
                    </b>
                    <small>{m.name}</small>
                  </span>
                </span>
                <span className="x-num">{s ? fmtPrice(s.price) : '—'}</span>
                <span className={`x-num ${s?.changePct == null ? 'x-muted' : up ? 'x-up' : 'x-down'}`}>
                  {s?.changePct == null ? '—' : fmtPct(s.changePct)}
                </span>
                <span className="x-num x-muted">{s ? fmtUsd(s.volume24h, { compact: true }) : '—'}</span>
              </div>
            );
          })}
          {rows.length === 0 && (
            <div className="x-empty">{cat === 'fav' ? 'Star a market to pin it here.' : 'No markets match.'}</div>
          )}
        </div>
      </Popover>
    </>
  );
}
